import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type { AnalyticsEventPayload, EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics';
import type { RelativeSelectionPos } from '@atlaskit/editor-common/selection';
import type { EditorState, Selection, Transaction } from '@atlaskit/editor-prosemirror/state';

type TableEditorAnalyticsAPI = {
	analyticsCallback: (
		contentId,
		createAnalyticsEvent?: CreateUIAnalyticsEvent,
	) => EditorAnalyticsAPI;
};

const normalizePayload = (payload: AnalyticsEventPayload) => {
	const typeMap = {
		operational: 'sendOperationalEvent',
		screen: 'sendScreenEvent',
		track: 'sendTrackEvent',
		ui: 'sendUIEvent',
	};

	return {
		//adding "type" and "source", as they're originally omitted but expected by createAnalyticsEvent
		data: {
			...payload,
			source: 'editorComponent',
		},
		type: typeMap[payload.eventType],
	};
};

export const editorAnalyticsAPI: TableEditorAnalyticsAPI = {
	analyticsCallback: (contentId, createAnalyticsEvent?: CreateUIAnalyticsEvent) => {
		const fireAnalyticsEvent = (payload: AnalyticsEventPayload) => {
			if (
				!process.env.REACT_SSR &&
				typeof window !== 'undefined' &&
				typeof window.requestAnimationFrame === 'function' &&
				createAnalyticsEvent
			) {
				window.requestAnimationFrame(() => {
					const analyticsPayload = normalizePayload(payload);
					if (contentId) {
						analyticsPayload.data.attributes = {
							...analyticsPayload.data.attributes,
							objectId: contentId,
						};
					}
					createAnalyticsEvent(analyticsPayload).fire();
				});
			}
		};

		const attachAnalyticsEvent = (payload: AnalyticsEventPayload) => {
			fireAnalyticsEvent(payload);

			return (_tr: Transaction) => {
				return true;
			};
		};

		return { attachAnalyticsEvent, fireAnalyticsEvent };
	},
};

export const editorSelectionAPI = {
	getSelectionPluginState: (state: EditorState) => {
		return (state as any)['selection$'];
	},

	setSelectionRelativeToNode:
		({
			selectionRelativeToNode,
			selection,
		}: {
			selectionRelativeToNode?: RelativeSelectionPos;
			selection?: Selection | null;
		}) =>
		(state: EditorState) => {
			const tr = state.tr;
			tr.setMeta('selection$', {
				type: 'SET_RELATIVE_SELECTION',
				selectionRelativeToNode,
			});

			if (selection) {
				tr.setSelection(selection);
			}

			return tr;
		},
};
